import {
  isCompanyArchived,
  isCompanyAssignedToUser,
} from "./companyListUtils";
import { toLocalDateInputIso } from "./subscriptionPeriodDate";

export const DASHBOARD_SUBSCRIPTION_MAX_PERIOD = 3650;

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

/** @param {Array} companies @param {string|number|null|undefined} currentUserId */
/** @param {object} company */
export function getDashboardEntityCountry(company) {
  return company?.country_id_rel?.tag_country ?? "RU";
}

/**
 * @param {object} company
 * @param {{
 *   canShowLicensedServers: boolean,
 *   serversPending: boolean,
 *   softAllData: Array,
 *   licensingAll: Array,
 * }} ctx
 * @returns {'neutral'|'green'|'yellow'|'red'}
 */
export function getDashboardStripeLevelForEntity(company, ctx) {
  const subscription = formatDashboardSubscription(company.subscription_rel);
  const {
    canShowLicensedServers,
    serversPending,
    softAllData,
    licensingAll,
  } = ctx;

  if (serversPending) {
    return getDashboardCardStripeLevel({
      isSubscriptionActive: subscription.isActive,
      serverLevels: null,
      serversPending: true,
    });
  }

  let serverLevels = [];
  if (!canShowLicensedServers) {
    serverLevels = [];
  } else {
    const servers = buildLicensedServersForInn(
      company.inn,
      softAllData,
      licensingAll,
    );
    serverLevels = servers.map((row) =>
      getSessionIndicatorLevel(row.last_session),
    );
  }

  return getDashboardCardStripeLevel({
    isSubscriptionActive: subscription.isActive,
    serverLevels,
    serversPending: false,
  });
}

export function listMyDashboardEntities(companies, currentUserId) {
  if (!Array.isArray(companies)) {
    return [];
  }
  return companies.filter(
    (company) =>
      isCompanyAssignedToUser(company, currentUserId) &&
      !isCompanyArchived(company),
  );
}

/** @param {null|undefined|Array} subscriptionRel */
export function formatDashboardSubscription(subscriptionRel) {
  if (!subscriptionRel?.length) {
    return { text: "Подписка не активна", isActive: false };
  }
  const validityDate = subscriptionRel[0].validity_date;
  const dateSubscription = new Date(validityDate).getTime();
  if (Number.isNaN(dateSubscription)) {
    return { text: "Подписка не активна", isActive: false };
  }
  const isActive = dateSubscription > Date.now();
  if (!isActive) {
    return { text: "Подписка не активна", isActive: false };
  }
  const iso = toLocalDateInputIso(validityDate);
  if (!iso) {
    return { text: "Подписка не активна", isActive: false };
  }
  const [y, m, d] = iso.split("-");
  const yy = y.slice(-2);
  return { text: `Подписка активна до ${d}.${m}.${yy}`, isActive: true };
}

/** @param {unknown} last_session */
export function formatDashboardSessionDateTime(last_session) {
  if (getSessionIndicatorLevel(last_session) === "none") {
    return null;
  }
  const sessionTime = new Date(last_session).getTime();
  if (Number.isNaN(sessionTime)) {
    return null;
  }
  const date = new Date(sessionTime);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear() % 100).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yy} ${hh}:${min}`;
}

/** @param {'fresh'|'warn'|'stale'|'none'} level */
export function getSessionStatusDotClass(level) {
  switch (level) {
    case "fresh":
      return "dot-success";
    case "warn":
      return "warn";
    case "stale":
    case "none":
      return "dot-error";
    default:
      return "dot-error";
  }
}

/**
 * @param {{
 *   isSubscriptionActive: boolean,
 *   serverLevels: Array<'fresh'|'warn'|'stale'|'none'>|null,
 *   serversPending?: boolean
 * }} params
 * @returns {'neutral'|'green'|'yellow'|'red'}
 */
export function getDashboardCardStripeLevel({
  isSubscriptionActive,
  serverLevels,
  serversPending = false,
}) {
  if (serversPending) {
    return "neutral";
  }
  const levels = Array.isArray(serverLevels) ? serverLevels : [];
  if (levels.length === 0) {
    return isSubscriptionActive ? "green" : "yellow";
  }
  if (levels.some((l) => l === "stale" || l === "none")) {
    return "red";
  }
  if (
    levels.some((l) => l === "warn") ||
    (!isSubscriptionActive && levels.every((l) => l === "fresh"))
  ) {
    return "yellow";
  }
  return "green";
}

/**
 * @param {string} inn
 * @param {Array} softAllData
 * @param {Array} licensingAll
 */
export function buildLicensedServersForInn(inn, softAllData, licensingAll) {
  if (!Array.isArray(softAllData) || !Array.isArray(licensingAll)) {
    return [];
  }
  const innStr = String(inn);
  const licensingByClientId = new Map(
    licensingAll.map((l) => [l.software_client_id, l]),
  );
  const rows = [];
  for (const el of softAllData) {
    if (String(el.company_rel?.inn) !== innStr) {
      continue;
    }
    if (el.software_client_rel?.type !== "SRV") {
      continue;
    }
    const license = licensingByClientId.get(el.client_id);
    if (!license) {
      continue;
    }
    const displayName =
      el.software_client_rel?.name ??
      el.software_name ??
      el.name ??
      "";
    rows.push({
      displayName,
      software_version: el.software_version ?? "",
      last_session: el.last_session,
    });
  }
  return rows;
}

/** @param {unknown} last_session */
export function getSessionIndicatorLevel(last_session) {
  if (last_session == null || last_session === "") {
    return "none";
  }
  const sessionTime = new Date(last_session).getTime();
  if (Number.isNaN(sessionTime)) {
    return "none";
  }
  const ageMs = Date.now() - sessionTime;
  if (ageMs < MS_DAY) {
    return "fresh";
  }
  if (ageMs <= 5 * MS_DAY) {
    return "warn";
  }
  return "stale";
}

/**
 * @param {Array} companies
 * @param {Array|null|undefined} reportRows
 * @param {Set<string>|Iterable<string>} innSet
 */
export function enrichDashboardCompaniesSubscription(
  companies,
  reportRows,
  innSet,
) {
  if (!Array.isArray(companies)) {
    return [];
  }
  const allowed = innSet instanceof Set ? innSet : new Set(innSet);
  const rows = Array.isArray(reportRows) ? reportRows : [];
  const byInn = new Map();
  for (const row of rows) {
    const rowInn = row?.company_rel?.inn;
    if (rowInn == null) {
      continue;
    }
    const key = String(rowInn);
    if (!allowed.has(key)) {
      continue;
    }
    if (!byInn.has(key) && row.validity_date != null) {
      byInn.set(key, row.validity_date);
    }
  }
  return companies.map((company) => {
    if (company.subscription_rel?.[0]?.validity_date != null) {
      return company;
    }
    const key = String(company.inn);
    const validity_date = byInn.get(key);
    if (validity_date == null) {
      return company;
    }
    return {
      ...company,
      subscription_rel: [{ validity_date }],
    };
  });
}
